import { createContext, useState, useEffect, useContext } from 'react';
import { getPreferredTemplate } from '../api/user';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Validate token by making an authenticated API call
  const validateToken = async (authToken) => {
    if (!authToken) return false;
    
    try {
      // Use a lightweight endpoint to validate token
      await getPreferredTemplate(authToken);
      return true;
    } catch (error) {
      // Token is invalid or expired
      if (error.response?.status === 401) {
        console.log('[AuthContext] Token validation failed - token expired or invalid');
        return false;
      }
      // For other errors, assume token is still valid (network issues, etc.)
      return true;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const savedToken = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('user');

      if (savedToken && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          const normalizedUser = parsedUser && typeof parsedUser === 'object'
            ? { ...parsedUser, id: parsedUser.id || parsedUser._id || null }
            : null;
          if (normalizedUser) {
            const isValid = await validateToken(savedToken);
            if (isValid) {
              setToken(savedToken);
              setCurrentUser(normalizedUser);
              localStorage.setItem('user', JSON.stringify(normalizedUser));
            } else {
              console.log('[AuthContext] Clearing invalid token from localStorage');
              localStorage.removeItem('authToken');
              localStorage.removeItem('user');
            }
          }
        } catch (err) {
          console.error('Failed to parse saved user JSON:', err);
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
        }
      }

      setIsLoading(false);
    };

    initializeAuth();

    const handleStorageChange = (e) => {
      if (e.key === 'authToken' || e.key === 'user') {
        if (!e.newValue) {
          setToken(null);
          setCurrentUser(null);
        } else if (e.key === 'authToken') {
          setToken(e.newValue);
        } else if (e.key === 'user') {
          try {
            const parsedUser = JSON.parse(e.newValue);
            const normalizedUser = parsedUser && typeof parsedUser === 'object'
              ? { ...parsedUser, id: parsedUser.id || parsedUser._id || null }
              : null;
            setCurrentUser(normalizedUser);
          } catch (err) {
            console.error('Failed to parse user from storage event:', err);
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const handleTokenCleared = () => {
      console.log('[AuthContext] Token cleared event received from API interceptor');
      setToken(null);
      setCurrentUser(null);
    };

    window.addEventListener('authTokenCleared', handleTokenCleared);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authTokenCleared', handleTokenCleared);
    };
  }, []);

  const login = (userData, authToken) => {
    if (!userData || typeof userData !== 'object') return;

    const normalizedUser = { ...userData, id: userData._id || userData.id };
    setCurrentUser(normalizedUser);
    setToken(authToken);
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
  };

  const logout = () => {
    setCurrentUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ 
      currentUser, 
      token, 
      login, 
      logout,
      isLoading
    }}>
      {children}
    </AuthContext.Provider>
  );
};
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);
